import React, { useState, useEffect } from 'react';
import axiosConfig from "../../../api/axiosConfig";

const AdminManagementPage = () => {
  const [admins, setAdmins] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    menu_permissions: []
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Menü listesi - Sidebar'dan alınmış
  const menuItems = [
    { key: 'dashboard', label: 'Anasayfa', parent: 'Genel' },
    { key: 'advisors', label: 'Danışman Listesi', parent: 'Genel' },
    { key: 'companies', label: 'Şirket Listesi', parent: 'Genel' },
    { key: 'students', label: 'Tüm Öğrenciler', parent: 'Öğrenci Yönetimi' },
    { key: 'teachers', label: 'Tüm Öğretmenler', parent: 'Öğretmen Yönetimi' },
    { key: 'past-lessons', label: 'Geçmiş Dersler', parent: 'Öğretmen Yönetimi' },
    { key: 'kurs-satis', label: 'Yeni Ders Ücretleri', parent: 'Öğretmen Yönetimi' },
    { key: 'mail-sms', label: 'Mail & SMS Gönder', parent: 'Mail & SMS' },
    { key: 'lesson-groups', label: 'Ders Grupları', parent: 'Ders Yönetimi' },
    { key: 'admin-list', label: 'Admin Listesi', parent: 'Kullanıcı Yönetimi' },
    { key: 'avans', label: 'Avans Yönetimi', parent: 'Muhasebe' },
    { key: 'gelen-odemeler', label: 'Gelen Ödemeler', parent: 'Muhasebe' },
    { key: 'teacher-report', label: 'Öğretmen Raporu', parent: 'Muhasebe/Raporlar' },
    { key: 'student-report', label: 'Öğrenci Raporu', parent: 'Muhasebe/Raporlar' },
    { key: 'danismanlar-report', label: 'Danışmanlar Raporu', parent: 'Muhasebe/Raporlar' },
    { key: 'companies-report', label: 'Şirketler Raporu', parent: 'Muhasebe/Raporlar' },
    { key: 'todo', label: 'Görev Listesi', parent: 'Yapılacaklar' }
  ];

  // Menüleri kategoriye göre grupla
  const groupedMenus = menuItems.reduce((acc, item) => {
    if (!acc[item.parent]) {
      acc[item.parent] = [];
    }
    acc[item.parent].push(item);
    return acc;
  }, {});

  // Sayfa yüklendiğinde verileri çek
  useEffect(() => {
    fetchAdmins();
  }, []);

  // Admin listesini sunucudan çek
  const fetchAdmins = async () => {
    setLoading(true);
    setError(null);
    
    const token = localStorage.getItem('token');
    
    try {
      const response = await axiosConfig.get('/admin/all', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setAdmins(response.data);
      setSelectedIds([]);
      
    } catch (error) {
      console.error('Hata oluştu:', error);
      
      if (error.response) {
        if (error.response.status === 401) {
          setError('🔐 YETKİLENDİRME HATASI: Token geçersiz veya süresi dolmuş. Tekrar giriş yapın.');
          setTimeout(() => {
            localStorage.clear();
            window.location.href = '/admin/login';
          }, 3000);
        } else {
          setError(`❌ HTTP ${error.response.status}: ${error.response.data?.message || error.response.statusText}`);
        }
      } else {
        setError('🌐 BAĞLANTI HATASI: Sunucuya ulaşılamıyor.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Yeni admin eklemek için formu aç
  const openFormForCreate = () => {
    setCurrentAdmin(null);
    setFormData({ name: '', email: '', password: '', menu_permissions: [] });
    setFormErrors({});
    setIsFormOpen(true);
  };

  // Mevcut bir admini düzenlemek için formu aç
  const openFormForEdit = (admin) => {
    setCurrentAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      password: '',
      menu_permissions: admin.menu_permissions || []
    });
    setFormErrors({});
    setIsFormOpen(true);
  };

  // Formu kapat
  const closeForm = () => {
    setIsFormOpen(false);
    setCurrentAdmin(null);
    setFormData({ name: '', email: '', password: '', menu_permissions: [] });
    setFormErrors({});
  };

  // Form validasyonu
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Ad soyad gereklidir';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email gereklidir';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Geçerli bir email adresi giriniz';
    }
    
    // Yeni admin eklenirken veya şifre güncellenirken kontrol
    if (!currentAdmin || formData.password) {
      if (!formData.password) {
        errors.password = 'Şifre gereklidir';
      } else if (formData.password.length < 6) {
        errors.password = 'Şifre en az 6 karakter olmalıdır';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Ekleme/Güncelleme form submit
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    const token = localStorage.getItem('token');
    
    try {
      if (currentAdmin) {
        // Güncelle
        const updateData = {
          name: formData.name,
          email: formData.email,
          menu_permissions: formData.menu_permissions
        };
        
        // Şifre girilmişse ekle
        if (formData.password) {
          updateData.password = formData.password;
        }
        
        await axiosConfig.put(`/admin/all/${currentAdmin.id}`, updateData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        alert('Admin başarıyla güncellendi!');
      } else {
        // Ekle
        const createData = {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          menu_permissions: formData.menu_permissions
        };
        
        await axiosConfig.post('/admin/all', createData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        alert('Admin başarıyla eklendi!');
      }
      fetchAdmins();
      closeForm();
      
    } catch (error) {
      console.error('Submit hatası:', error);
      
      if (error.response) {
        if (error.response.status === 409) {
          setFormErrors({ email: 'Bu email adresi zaten kullanılıyor!' });
        } else if (error.response.status === 401) {
          alert('Yetkilendirme hatası! Lütfen tekrar giriş yapın.');
          setTimeout(() => {
            localStorage.clear();
            window.location.href = '/admin/login';
          }, 2000);
        } else {
          alert(`Hata: ${error.response.data?.message || 'Bir hata oluştu'}`);
        }
      } else {
        alert('Sunucuya bağlanılamıyor!');
      }
    } finally {
      setLoading(false);
    }
  };

  // Tekil silme
  const handleDelete = async (id) => {
    // Kendi kendini silmeye çalışıyor mu kontrol et
    const currentAdminId = localStorage.getItem('adminId');
    if (id.toString() === currentAdminId) {
      alert('Kendi hesabınızı silemezsiniz!');
      return;
    }
    
    if (!window.confirm('Bu admini silmek istediğinizden emin misiniz?')) return;
    
    setLoading(true);
    setError(null);
    
    const token = localStorage.getItem('token');
    
    try {
      await axiosConfig.delete(`/admin/all/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      alert('Admin başarıyla silindi!');
      fetchAdmins();
      
    } catch (error) {
      console.error('Delete hatası:', error);
      
      if (error.response) {
        if (error.response.status === 401) {
          alert('🔐 Yetkilendirme hatası! Tekrar giriş yapın.');
          setTimeout(() => {
            localStorage.clear();
            window.location.href = '/admin/login';
          }, 2000);
        } else {
          alert(`Hata: ${error.response.data?.message || 'Silme işlemi başarısız'}`);
        }
      } else {
        alert('🌐 Sunucuya bağlanılamıyor!');
      }
    } finally {
      setLoading(false);
    }
  };

  // Toplu silme
  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    
    // Kendi ID'sini kontrol et
    const currentAdminId = localStorage.getItem('adminId');
    if (selectedIds.includes(parseInt(currentAdminId))) {
      alert('Kendi hesabınızı silemezsiniz! Lütfen seçimden çıkarın.');
      return;
    }
    
    if (!window.confirm(`${selectedIds.length} admini silmek istediğinizden emin misiniz?`)) return;

    setLoading(true);
    setError(null);
    
    const token = localStorage.getItem('token');
    
    const results = {
      success: [],
      failed: []
    };
    
    try {
      for (const id of selectedIds) {
        try {
          await axiosConfig.delete(`/admin/all/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          results.success.push(id);
        } catch (error) {
          results.failed.push({
            id,
            error: error.response?.data?.message || error.message
          });
        }
      }
      
      if (results.failed.length > 0) {
        const failedDetails = results.failed.map(f => `ID ${f.id}: ${f.error}`).join('\n');
        alert(`✅ Başarılı: ${results.success.length}\n❌ Başarısız: ${results.failed.length}\n\nHata detayları:\n${failedDetails}`);
      } else {
        alert(`✅ ${results.success.length} admin başarıyla silindi!`);
      }
      
      fetchAdmins();
      
    } catch (error) {
      console.error('Toplu silme hatası:', error);
      alert(`Toplu silme hatası: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Checkbox değişimi
  const handleSelectChange = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selId) => selId !== id) : [...prev, id]
    );
  };

  // Tüm checkbox'ları seç / kaldır
  const handleSelectAll = () => {
    if (selectedIds.length === admins.length) {
      setSelectedIds([]);
    } else {
      const allIds = admins.map((admin) => admin.id);
      setSelectedIds(allIds);
    }
  };

  // Menü yetkisi checkbox değişimi
  const handleMenuPermissionChange = (menuKey) => {
    setFormData(prev => ({
      ...prev,
      menu_permissions: prev.menu_permissions.includes(menuKey)
        ? prev.menu_permissions.filter(key => key !== menuKey)
        : [...prev.menu_permissions, menuKey]
    }));
  };

  // Tüm menüleri seç/kaldır
  const handleSelectAllMenus = () => {
    if (formData.menu_permissions.length === menuItems.length) {
      setFormData(prev => ({ ...prev, menu_permissions: [] }));
    } else {
      setFormData(prev => ({ ...prev, menu_permissions: menuItems.map(item => item.key) }));
    }
  };

  // Kategori bazlı seç/kaldır
  const handleSelectCategory = (category) => {
    const categoryMenus = menuItems.filter(item => item.parent === category).map(item => item.key);
    const allSelected = categoryMenus.every(key => formData.menu_permissions.includes(key));
    
    if (allSelected) {
      setFormData(prev => ({
        ...prev,
        menu_permissions: prev.menu_permissions.filter(key => !categoryMenus.includes(key))
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        menu_permissions: [...new Set([...prev.menu_permissions, ...categoryMenus])]
      }));
    }
  };

  // Filtrelenmiş admin listesi
  const filteredAdmins = admins.filter((admin) => {
    return admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           admin.email.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Mevcut admin kontrolü
  const currentAdminId = localStorage.getItem('adminId');

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-7xl mx-auto bg-white p-6 rounded-md shadow-md">
        {/* Hata mesajı */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* Üst kısım */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
          <h2 className="text-2xl font-bold text-gray-700">Admin Yönetimi - Menü Yetkileri</h2>
          <div className="flex gap-2">
            <button
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleBatchDelete}
              disabled={selectedIds.length === 0 || loading}
            >
              Seçili Adminleri Sil ({selectedIds.length})
            </button>
            <button
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
              onClick={openFormForCreate}
              disabled={loading}
            >
              Yeni Admin Ekle
            </button>
          </div>
        </div>

        {/* Arama */}
        <div className="mb-4">
          <input
            type="text"
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Ad veya email ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Admin Tablosu */}
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-200 text-gray-700">
                <th className="p-3 border border-gray-300 text-center">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === filteredAdmins.length && filteredAdmins.length > 0}
                    onChange={handleSelectAll}
                    disabled={loading}
                  />
                </th>
                <th className="p-3 border border-gray-300 text-left">ID</th>
                <th className="p-3 border border-gray-300 text-left">Ad Soyad</th>
                <th className="p-3 border border-gray-300 text-left">Email</th>
                <th className="p-3 border border-gray-300 text-left">Menü Yetkileri</th>
                <th className="p-3 border border-gray-300 text-left">Kayıt Tarihi</th>
                <th className="p-3 border border-gray-300 text-left">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="p-4 text-center text-gray-500">
                    Yükleniyor...
                  </td>
                </tr>
              ) : filteredAdmins.length > 0 ? (
                filteredAdmins.map((admin) => {
                  const isSelected = selectedIds.includes(admin.id);
                  const isCurrentAdmin = admin.id.toString() === currentAdminId;
                  return (
                    <tr key={admin.id} className={`hover:bg-gray-100 ${isCurrentAdmin ? 'bg-blue-50' : ''}`}>
                      <td className="p-3 border border-gray-200 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectChange(admin.id)}
                          disabled={loading}
                        />
                      </td>
                      <td className="p-3 border border-gray-200">
                        {admin.id}
                        {isCurrentAdmin && (
                          <span className="ml-2 text-xs bg-blue-500 text-white px-2 py-1 rounded">Sen</span>
                        )}
                      </td>
                      <td className="p-3 border border-gray-200">{admin.name}</td>
                      <td className="p-3 border border-gray-200">{admin.email}</td>
                      <td className="p-3 border border-gray-200">
                        {admin.menu_permissions && admin.menu_permissions.length > 0 ? (
                          <div className="text-xs">
                            <span className="font-semibold">{admin.menu_permissions.length} menü yetkisi</span>
                            <details className="mt-1">
                              <summary className="cursor-pointer text-blue-600 hover:text-blue-800">Detayları göster</summary>
                              <div className="mt-2 space-y-1">
                                {admin.menu_permissions.map(key => {
                                  const menuItem = menuItems.find(item => item.key === key);
                                  return (
                                    <div key={key} className="text-gray-600">
                                      • {menuItem ? `${menuItem.label} (${menuItem.parent})` : key}
                                    </div>
                                  );
                                })}
                              </div>
                            </details>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">Yetki yok</span>
                        )}
                      </td>
                      <td className="p-3 border border-gray-200">
                        {admin.created_at ? new Date(admin.created_at).toLocaleDateString('tr-TR') : '-'}
                      </td>
                      <td className="p-3 border border-gray-200">
                        <button
                          className="bg-blue-500 text-white px-3 py-1 rounded mr-2 hover:bg-blue-600 disabled:opacity-50"
                          onClick={() => openFormForEdit(admin)}
                          disabled={loading}
                        >
                          Düzenle
                        </button>
                        <button
                          className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 disabled:opacity-50"
                          onClick={() => handleDelete(admin.id)}
                          disabled={loading || isCurrentAdmin}
                          title={isCurrentAdmin ? "Kendi hesabınızı silemezsiniz" : ""}
                        >
                          Sil
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="p-4 text-center text-gray-500">
                    Kayıt bulunamadı.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-md shadow-md w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4 text-gray-700">
              {currentAdmin ? 'Admini Düzenle' : 'Yeni Admin Ekle'}
            </h3>
            <div>
              {/* Ad Soyad */}
              <div className="mb-4">
                <label htmlFor="name" className="block mb-1 font-medium text-gray-600">
                  Ad Soyad
                </label>
                <input
                  id="name"
                  type="text"
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-1 ${
                    formErrors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={loading}
                />
                {formErrors.name && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
                )}
              </div>

              {/* Email */}
              <div className="mb-4">
                <label htmlFor="email" className="block mb-1 font-medium text-gray-600">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-1 ${
                    formErrors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={loading}
                />
                {formErrors.email && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                )}
              </div>

              {/* Şifre */}
              <div className="mb-4">
                <label htmlFor="password" className="block mb-1 font-medium text-gray-600">
                  Şifre {currentAdmin && <span className="text-sm text-gray-500">(Boş bırakırsanız değişmez)</span>}
                </label>
                <input
                  id="password"
                  type="password"
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-1 ${
                    formErrors.password ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={currentAdmin ? "Yeni şifre (opsiyonel)" : "Şifre giriniz"}
                  disabled={loading}
                />
                {formErrors.password && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.password}</p>
                )}
              </div>

              {/* Menü Yetkileri */}
              <div className="mb-4">
                <label className="block mb-2 font-medium text-gray-600">
                  Menü Yetkileri
                </label>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Admin sadece seçili menüleri görebilir
                  </p>
                  <button
                    type="button"
                    onClick={handleSelectAllMenus}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {formData.menu_permissions.length === menuItems.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                  </button>
                </div>
                <div className="border border-gray-300 rounded p-4 max-h-60 overflow-y-auto">
                  {Object.entries(groupedMenus).map(([category, items]) => (
                    <div key={category} className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-700">{category}</h4>
                        <button
                          type="button"
                          onClick={() => handleSelectCategory(category)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          {items.every(item => formData.menu_permissions.includes(item.key)) ? 'Kaldır' : 'Seç'}
                        </button>
                      </div>
                      <div className="space-y-1 ml-4">
                        {items.map(item => (
                          <label key={item.key} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                            <input
                              type="checkbox"
                              className="mr-2"
                              checked={formData.menu_permissions.includes(item.key)}
                              onChange={() => handleMenuPermissionChange(item.key)}
                              disabled={loading}
                            />
                            <span className="text-gray-700 text-sm">{item.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Butonlar */}
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500 disabled:opacity-50"
                  onClick={closeForm}
                  disabled={loading}
                >
                  İptal
                </button>
                <button
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? 'İşleniyor...' : (currentAdmin ? 'Güncelle' : 'Kaydet')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagementPage;